import React from "react";
import { DataSource } from "../config/dataSources";
import SourceTick from "./SourceTick";

interface DataSourceItemProps {
	dataSource: DataSource;
	onSelect: (dataSource: DataSource) => void;
	isSelected?: boolean;
	/**
	 * "Demo snapshot · synced 2h ago" when this system already has data behind
	 * it; `undefined` when it has never been connected. Presence is what earns
	 * the tick. No item counts — epic locked decision 5.
	 */
	connectedDetail?: string;
	/**
	 * This system is connected but its sync has stopped keeping up (derived
	 * from freshness — see `utils/sourceStatus`). It takes the tick away: a
	 * tick beside a broken sync is the specific lie this replaces.
	 */
	syncIssue?: boolean;
	/**
	 * A live connection (a `cloud_accounts` row) exists for this system on this
	 * firm. Distinct from `connectedDetail`, which only says the system has
	 * data — a demo-seeded tenant has data it never connected. Connecting is a
	 * once-per-firm onboarding event, so a connected system stops offering the
	 * button rather than sending the user into a 409.
	 */
	isConnectionLocked?: boolean;
	reauthorizationRequired?: boolean;
}

const DataSourceItem: React.FC<DataSourceItemProps> = ({
	dataSource,
	onSelect,
	isSelected = false,
	connectedDetail,
	syncIssue = false,
	isConnectionLocked = false,
	reauthorizationRequired = false,
}) => {
	// Three independent facts, deliberately not collapsed: whether the system
	// HAS DATA (earns a tick), whether it IS CONNECTABLE (has a built OAuth
	// handler), and whether it is already CONNECTED (the backend will refuse a
	// second connection). A demo-seeded Box has data and a handler but no
	// connection — it must keep its Reconnect affordance, or the real connector
	// is unreachable on every seeded build.
	const hasData = Boolean(connectedDetail);
	const isSelectable = dataSource.isAvailable && !isConnectionLocked;
	// A locked row is finished, not unbuilt: it must never inherit the muted
	// "coming soon" treatment.
	const isComingSoon = !dataSource.isAvailable && !hasData;

	const handleClick = () => {
		if (isSelectable) {
			onSelect(dataSource);
		}
	};

	// The accessible name must match what the row can actually do — a disabled
	// row announcing "Connect to …" is a promise it cannot keep.
	//
	// The mixed state is deliberate, not inherited: a system with no in-app
	// connect path can still have been wired up for the firm out-of-band
	// (SharePoint's org-level app registration lives in Microsoft's console,
	// not in this dialog). Such a row is neither "coming soon" nor something
	// the user can act on, so it says who set it up and stops there.
	const label = reauthorizationRequired
		? `Reconnect ${dataSource.name} — authorization expired`
		: syncIssue
		? `${dataSource.name} — sync issue; contact support`
		: isConnectionLocked
		? `${dataSource.name} — connected; contact support to change the connection`
		: isSelectable
		? hasData
			? `Reconnect ${dataSource.name} — already syncing`
			: `Connect to ${dataSource.name}`
		: hasData
		? `${dataSource.name} — syncing; set up for your firm by Doppler support`
		: `${dataSource.name} — coming soon`;

	return (
		<div
			className={`flex h-[45px] w-full items-center gap-3 rounded-lg px-4 transition-all duration-200 ${
				isSelectable
					? "cursor-pointer hover:bg-[#EEEEEE] active:bg-[#E8E8E8]"
					: isConnectionLocked || hasData
					? "cursor-default"
					: "cursor-not-allowed opacity-50"
			} ${
				isSelected ? "border border-[#D0D0D0] bg-[#E8E8E8]" : "bg-[#F6F6F6]"
			}`}
			onClick={handleClick}
			role="button"
			tabIndex={isSelectable ? 0 : -1}
			aria-disabled={!isSelectable}
			aria-label={label}
			onKeyDown={(e) => {
				if ((e.key === "Enter" || e.key === " ") && isSelectable) {
					e.preventDefault();
					handleClick();
				}
			}}
		>
			<div className="flex h-[25px] w-[25px] flex-shrink-0 items-center justify-center">
				<img
					src={dataSource.icon}
					alt=""
					aria-hidden
					className="max-h-[25px] max-w-[25px] object-contain"
				/>
			</div>

			<span
				className={`flex-1 truncate text-left text-[15px] leading-[25px] ${
					isComingSoon ? "text-gray-400" : "text-[#4B5563]"
				}`}
			>
				{dataSource.name}
			</span>

			{reauthorizationRequired ? (
				<span className="flex-shrink-0 text-xs font-medium text-amber-700">
					Reconnect required
				</span>
			) : syncIssue && connectedDetail ? (
				<span
					className="flex-shrink-0 text-xs font-medium text-amber-700"
					title={connectedDetail}
					aria-label={`${dataSource.name}: ${connectedDetail}`}
				>
					Sync issue — contact support
				</span>
			) : hasData && connectedDetail ? (
				<SourceTick label={dataSource.name} detail={connectedDetail} />
			) : isConnectionLocked ? (
				// A connection can exist before its first sync has registered
				// anything, so a locked row has to say something without a tick
				// to hang the detail on.
				//
				// It says "awaiting first sync", not "Connected", because those
				// are different claims and only the weaker one is known here.
				// A locked tile with no registry row means a `cloud_accounts`
				// row exists and no sync has ever been proven — and if the
				// grant was dead on arrival, no sync ever will be, so a bare
				// "Connected" is a permanent lie with nothing to age it out.
				// This wording is true on the first fifteen minutes and still
				// true, and now actionable, a month later.
				<span className="flex-shrink-0 text-xs font-medium text-text-muted">
					Connected · awaiting first sync
				</span>
			) : isComingSoon ? (
				<span className="flex-shrink-0 text-xs font-medium text-gray-400">
					Coming soon
				</span>
			) : null}
		</div>
	);
};

export default DataSourceItem;
